import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth/store';
import { Loader2 } from 'lucide-react';
import { useCurrentUserModules } from '@/lib/permissions/use-current-modules';
import { useAclSettingDetail, useEffectiveAclId } from '@/features/acl/api';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { hasPermission, usePermissionStore } from '@/lib/permissions/store';

/**
 * Redirects unauthenticated users to /login while preserving the intended
 * destination as ?from=… so we can bounce back after a successful login.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthed = useAuthStore((s) => s.isAuthenticated());
  const location = useLocation();

  if (!isAuthed) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

/** Inverse — bounces authed users away from /login. */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const isAuthed = useAuthStore((s) => s.isAuthenticated());
  if (isAuthed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Blocks a route unless the logged-in user has the module assigned.
 * SADMIN / SUB_SUPER_ADMIN are not module-gated (modules === null).
 */
export function RequireModule({
  moduleId,
  children,
}: {
  moduleId: number;
  children: React.ReactNode;
}) {
  const { modules, isLoading } = useCurrentUserModules();
  if (modules === null) return <>{children}</>;
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!modules.has(moduleId)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * TTS Studio gate.
 *  - sub-super-admin : own ACL must grant `aclPath` (default: parent "TTS Studio")
 *  - end user        : nearest sub-super-admin's ACL must grant "TTS Studio"
 *  - everyone else   : pass-through
 */
export function RequireTtsStudio({
  children,
  aclPath = 'TTS Studio',
}: {
  children: React.ReactNode;
  aclPath?: string;
}) {
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.userId);
  const perms = usePermissionStore((s) => s.perms);
  const isUser = role === USER_ROLES.USER;
  const effective = useEffectiveAclId(isUser ? userId : null);
  const aclId = effective.data?.aclId ?? null;
  const detail = useAclSettingDetail(isUser ? aclId : null);

  if (role === USER_ROLES.SUB_SUPER_ADMIN) {
    return hasPermission(perms, aclPath, BASE_PERMISSION.VIEW) ? (
      <>{children}</>
    ) : (
      <Navigate to="/" replace />
    );
  }

  if (isUser) {
    if (effective.isLoading || (aclId != null && detail.isLoading)) {
      return (
        <div className="flex h-full items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (aclId == null) return <>{children}</>; // no sub-super-admin above → unrestricted
    let allowed = false;
    try {
      const raw = detail.data?.moduleOperation;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      allowed = Boolean(parsed?.['TTS Studio']?.view_permission);
    } catch {
      allowed = false;
    }
    if (!allowed) return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
